import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { DrawerLink } from "../../app/DrawerLink";

import { Alert } from "../../components/ui/Alert";
import { Button } from "../../components/ui/Button";
import { LoadingPanel } from "../../components/ui/Loader";
import { ApiError } from "../../lib/api";
import { FavoriteButton } from "../../components/ui/FavoriteButton";
import { useRoomTuning } from "../../stores/room";
import { useFeature } from "../../stores/session";
import { StudioColumn } from "../studio/StudioColumn";
import { useStudioSession } from "../studio/StudioSession";
import { useStudioSettings } from "../../stores/studio";
import { RoomSettingsDialog } from "./RoomSettingsDialog";
import { SubmissionsControl } from "./SubmissionsControl";
import { Artwork } from "../dashboard/Artwork";
import { ChatPanel } from "./ChatPanel";
import { CrateDialog } from "./CrateDialog";
import { isListeningTo, startListening, stopListening } from "./listening";
import { QueueTable } from "./QueueTable";
import { ReviewPanel } from "./ReviewPanel";
import { SessionPanel } from "./SessionPanel";
import { positionNow } from "./sync";
import { useRoom } from "./useRoom";
import { duration, elapsed, rooms, type Room, type RoomView } from "./api";

/**
 * The DJ console (dev/design/GetVibed DJ Console.dc.html).
 *
 * ## What is here and what is not
 *
 * The design draws a mic toggle, a crossfade slider and a mic/music balance.
 * None of them are here, because nothing is mixing audio: a room broadcasts a
 * *position* in the catalogue, not a stream, so there is no signal path for a
 * voice to sit in. Drawing the controls anyway would be three dead sliders on
 * the busiest screen in the product.
 *
 * They arrive with `source: 'stream'`, which the schema already carries.
 *
 * The payout panel is likewise absent: boosts have tiers and no money, so there
 * is no figure to show that would not be invented.
 *
 * ## The transport moves the room, not this browser
 *
 * Pressing skip here advances the *room's* playhead for everybody. It does not
 * start audio in this tab - the console is silent for now, and the synchronised
 * player that makes a host hear what their listeners hear is the next piece.
 * That distinction is worth keeping straight even once audio lands: the deck is
 * a remote control, not a media player.
 */
export function ConsolePage() {
  const queryClient = useQueryClient();

  const [crateOpen, setCrateOpen] = useState(false);
  /*
   * Whether the studio is open, held here rather than derived from
   * `room.source`.
   *
   * The room is polled, so a response that arrived mid-rollover could read
   * `catalogue` for one render - and deriving from it would unmount the studio,
   * which takes the render loop, the camera and the peer connection with it. A
   * live broadcast must not be able to end because of a poll.
   */
  /*
   * Open/closed lives in the store, not here.
   *
   * The engine is mounted in the app shell so a broadcast survives navigation
   * (see `StudioSession`), which means the shell and this page both need to
   * know whether the studio is open - and this page is the one that unmounts.
   */
  const studioOpen = useStudioSettings((s) => s.open);
  const setStudioOpen = useStudioSettings((s) => s.setOpen);
  const session = useStudioSession();
  /** A mode change in flight. Both header controls wait for it. */
  const [switching, setSwitching] = useState(false);
  const studioLive = session.broadcasting;
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Subscribed rather than read once, so the header repaints when it changes.
  const tuned = useRoomTuning((state) => state.tuned);

  /*
   * `mine` rather than a username in the URL. One person has one station, so
   * there is nothing to choose between - and this endpoint creates it on first
   * use, which is why a host never fills in a form to start broadcasting.
   */
  const { data, isPending, error } = useRoom(undefined, {
    listen: true,
    mine: true,
  });

  if (isPending) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16">
        <LoadingPanel label="Opening the console" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16">
        <Alert tone="error">
          {error instanceof ApiError
            ? error.message
            : "That room could not be loaded."}
        </Alert>
      </div>
    );
  }

  const room = data.room;

  if (!room) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col items-start gap-4 px-6 py-16">
        <Alert tone="error">You are not set up to host.</Alert>
        <Link to="/host" className="text-sm text-accent hover:underline">
          Apply to host →
        </Link>
      </div>
    );
  }

  /**
   * Change where the broadcast comes from.
   *
   * Off air this only opens or closes the panel - nothing is live, so there is
   * nothing to switch. On air it moves the room between the catalogue and the
   * studio, which the server does without ending the broadcast, and stops the
   * publisher on the way out so a room in catalogue mode is not still being
   * fed by a camera nobody is watching.
   */
  const changeMode = async (next: "audio" | "studio") => {
    setSwitching(true);

    try {
      setStudioOpen(next === "studio");

      if (room.status !== "live") return;

      if (next === "audio") await session.deck.stop();

      await rooms.goLive(next === "studio" ? "stream" : "catalogue");
      await refresh();
    } catch {
      // The room refused the switch, which the next poll will show. Nothing
      // useful to say here that the room state will not say better.
    } finally {
      setSwitching(false);
    }
  };

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["room", "mine"] });

  return (
    /*
      Exactly a screen tall, and nothing outside a pane scrolls.

      Three columns that each grow to fit made the *document* the scroller, so
      reading the bottom of a long queue scrolled the deck and the chat away
      with it - on the one screen where all three are being watched at once.
      A console is an instrument panel, not a document.

      `h-` rather than `min-h-`, so the block cannot grow past the viewport, and
      `overflow-hidden` so a pane that overflows is that pane's problem. The
      height subtracts the header *and* the dock, which is fixed over the bottom
      76px of every page - without that the chat input lands underneath it.

      `-mb-6` cancels the shell's bottom padding, which would otherwise put a
      viewport-height block on a page 1.5rem taller than the viewport and give
      back the body scrollbar this exists to remove. Same recipe as Messages.

      The height subtracts `--spacing-chrome-top`, not `4rem`: the header is
      64px of content and a 65th of border, and a screen sized to `4rem` leaves
      exactly one pixel of page scroll - which is enough to keep the scrollbar
      and undo the whole thing.

      All of it from `lg` up. Below that the columns stack, and three panes
      sharing one screen's height would be three unusable slivers - so the page
      scrolls the way every other page does.
    */
    <div
      className="flex flex-col lg:-mb-6 lg:h-[calc(100dvh-var(--spacing-chrome-top)-var(--spacing-dock))] lg:overflow-hidden"
      data-console
    >
      <ConsoleHeader
        room={room}
        view={data}
        onChange={refresh}
        monitoring={tuned?.username === room.username}
        /*
         * The same global tuning a listener uses, so a host monitoring their own
         * broadcast gets the dock's live view and keeps hearing it after they
         * navigate away from the console.
         */
        onMonitor={() =>
          isListeningTo(room.username) ? stopListening() : startListening(room)
        }
        /*
         * The mode is a fact about the *room* once it is on air, and a
         * staged intention before that. Reading it from `room.source` while
         * live is what makes the pills a live switch rather than a second
         * source of truth that can disagree with the broadcast.
         */
        mode={
          room.status === "live"
            ? room.source === "stream"
              ? "studio"
              : "audio"
            : studioOpen
              ? "studio"
              : "audio"
        }
        onMode={(next) => void changeMode(next)}
        switching={switching}
        onSettings={() => setSettingsOpen(true)}
        studioLive={studioLive}
      />

      {/*
        Two columns, or three with the studio open.

        The left column exists only for the studio. It used to hold the deck and
        eight room settings, which made it a column doing three unrelated jobs -
        and the settings, decided once a set, were paying for permanent space
        out of the queue's budget. They are in a dialog now, the deck moved to
        the top of the middle column where what-is-playing sits above what-is-
        next, and the width freed up goes to the studio when there is one.

        The side columns are clamped rather than fixed: on a wide display a
        fixed 320px leaves them cramped while the queue runs to whitespace, so
        they grow with the viewport and stop.
      */}
      <div
        className={`grid flex-1 gap-px bg-line lg:min-h-0 ${
          studioOpen
            ? "lg:grid-cols-[minmax(0,var(--console-studio))_minmax(0,3fr)_minmax(0,var(--console-rail))]"
            : "lg:grid-cols-[minmax(0,3fr)_minmax(0,var(--console-rail))]"
        }`}
        style={
          {
            // The preview is the one thing here that gets worse as it narrows,
            // and a host judging their own framing in a 320px box will not
            // trust what they see.
            "--console-studio": "clamp(380px, 29vw, 520px)",
            "--console-rail": "clamp(340px, 26vw, 460px)",
          } as React.CSSProperties
        }
      >
        {studioOpen && <StudioColumn />}

        {/*
          What is playing, then what is next.

          One scroller for the pair rather than two: the deck is four lines and
          a picture, and giving it its own scrollbar beside the queue's would be
          two scrollbars in one column for the sake of a panel that never needs
          one. The deck does not shrink - `shrink-0` - so a long queue scrolls
          under a transport that stays put.
        */}
        <section
          className="flex min-w-0 flex-col bg-app lg:min-h-0"
          data-pane="queue"
        >
          <div className="shrink-0 border-b border-line p-6" data-deck>
            <Deck room={room} onChange={refresh} />
          </div>

          <div
            className="flex min-w-0 flex-col gap-5 p-6 lg:min-h-0 lg:overflow-y-auto"
            data-queue-scroll
          >
            <QueueTable
              room={room}
              view={data}
              onChange={refresh}
              onAdd={() => setCrateOpen(true)}
            />
          </div>
        </section>

        {/*
          No padding on the column itself - each scrolling child carries its
          own, so its scrollbar sits against the edge of the pane rather than
          floating 24px inside it with a strip of dead space beyond. A
          scrollbar drawn short of the edge it belongs to looks like a mistake,
          because it is one.
        */}
        <aside
          className="flex min-w-0 flex-col bg-app lg:min-h-0"
          data-pane="session"
        >
          {/*
            The figures keep their size and the chat takes what is left, so the
            message box sits at the bottom of the column rather than wherever
            the conversation happens to end. `ChatPanel` is already
            `flex-1 min-h-0` with its own scroller - it just needed a parent
            with a height to be a fraction of.
          */}
          {/*
            The figures take the column when there is no chat under them, and
            share it when there is. A `max-h` on a panel with nothing below it
            would be a scrollbar inside empty space.
          */}
          <div
            className={`overflow-y-auto px-6 pt-6 ${
              room.simulcast ? "min-h-0 flex-1" : "shrink-0 lg:max-h-[55%]"
            }`}
          >
            <SessionPanel room={room} view={data} />

            {/*
              Said, rather than the chat just being gone.

              A host who set a simulcast this morning and opens the console
              tonight would otherwise find a missing panel and no reason for it.
              One line is cheaper than the support question.
            */}
            {room.simulcast && (
              <p
                data-chat-elsewhere
                className="mt-6 border-t border-line pt-5 font-mono text-[10px] leading-relaxed text-ghost"
              >
                room chat is off while you are streaming on{" "}
                {room.simulcast.label} — the conversation is over there, and a
                second empty one here would only split it
              </p>
            )}
          </div>

          {!room.simulcast && (
            <div className="flex min-h-0 flex-1 flex-col pb-6 pt-6">
              <ChatPanel username={room.username} canModerate />
            </div>
          )}
        </aside>
      </div>

      {crateOpen && (
        <CrateDialog onClose={() => setCrateOpen(false)} onAdded={refresh} />
      )}

      {settingsOpen && (
        <RoomSettingsDialog
          room={room}
          onChange={refresh}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Header                                                                      */
/* -------------------------------------------------------------------------- */

function ConsoleHeader({
  room,
  view,
  onChange,
  monitoring,
  onMonitor,
  mode,
  onMode,
  switching,
  onSettings,
  studioLive,
}: {
  room: Room;
  view: RoomView;
  onChange: () => void;
  monitoring: boolean;
  onMonitor: () => void;
  mode: "audio" | "studio";
  onMode: (mode: "audio" | "studio") => void;
  /** A mode change is in flight, so neither control should be pressed again. */
  switching: boolean;
  onSettings: () => void;
  studioLive: boolean;
}) {
  const studio = useFeature("studio");

  // Ticks so the on-air clock counts. One second is the resolution it displays.
  const [, tick] = useState(0);

  useEffect(() => {
    if (room.status !== "live") return;

    const timer = setInterval(() => tick((n) => n + 1), 1000);

    return () => clearInterval(timer);
  }, [room.status]);

  const queryClient = useQueryClient();

  const broadcast = useMutation({
    mutationFn: () =>
      room.status === "live"
        ? rooms.endBroadcast()
        : rooms.goLive(mode === "studio" ? "stream" : "catalogue"),
    onSuccess: () => {
      onChange();

      /*
       * Everything that reports who is on air, refreshed now rather than on its
       * own schedule.
       *
       * `useLiveHosts` polls every 45 seconds, which is fine for noticing that
       * somebody *else* went live and useless for your own broadcast: the
       * header count, the dashboard rail and the account menu's "You're on air"
       * would all have kept insisting for the best part of a minute after this
       * host pressed End - and the one person guaranteed to be looking at them
       * is the one who just pressed it.
       */
      void queryClient.invalidateQueries({ queryKey: ["live-hosts"] });
    },
  });

  return (
    <header className="flex flex-wrap items-center gap-5 border-b border-line bg-deep px-6 py-3.5">
      {room.status === "live" ? (
        <span className="flex shrink-0 items-center gap-2.5 rounded-[var(--radius-pill)] border border-hot-edge bg-hot-wash px-4 py-2">
          <span className="h-2 w-2 rounded-full bg-hot" aria-hidden />
          <span className="meta-caps text-hot-text">
            On air · {elapsed(room.live_started_at)}
          </span>
        </span>
      ) : (
        <span className="shrink-0 rounded-[var(--radius-pill)] border border-line px-4 py-2">
          <span className="meta-caps text-faint">Off air</span>
        </span>
      )}

      <div className="flex min-w-0 flex-1 items-center gap-4">
        <div className="flex min-w-0 flex-col">
          {/* The set's name if it has one, otherwise the person - because the
              broadcast is theirs and that is enough of an identity. */}
          <span className="truncate text-base font-bold tracking-tight text-ink">
            {room.name ?? room.host.display_name}
          </span>
          <span className="truncate font-mono text-[11px] text-soft">
            /{room.username}
          </span>
        </div>

        {/* Beside the name, not in the settings dialog: this is the one room
            control a host changes mid-broadcast. See the note on the
            component. The wording that used to be under the name - "open
            submissions" - is now the control's own state. */}
        <SubmissionsControl room={room} onChange={onChange} />
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-6">
        <Stat value={room.listener_count} label="listening" />
        <Stat value={view.session.queued} label="in queue" tone="text-fresh" />
        <Stat value={view.session.boosted} label="boosted" tone="text-accent" />
      </div>

      {broadcast.isError && (
        <span className="text-sm text-hot-text">
          {broadcast.error instanceof ApiError
            ? broadcast.error.message
            : "That did not work."}
        </span>
      )}

      {/*
        A toggle, not a one-way door.

        It used to disappear once pressed, which left a host with no way to tell
        whether they were monitoring and no way to stop - and turning your own
        broadcast off in your ear is a thing hosts do constantly, because it is
        how you talk to somebody in the room without hearing yourself think.
      */}
      {room.status === "live" && !studioLive && (
        <Button
          variant={monitoring ? "secondary" : "primary"}
          onClick={onMonitor}
          data-monitor
          aria-pressed={monitoring}
        >
          {monitoring ? "Monitoring" : "Monitor"}
        </Button>
      )}

      {/*
        Gone while the studio is publishing, rather than disabled.

        This button tunes and untunes, and tuning is what keeps the deck
        playing into the mix - so pressing it mid-broadcast would silence the
        music for everybody listening. The studio has its own monitor control
        that only affects the host's speakers, which is what somebody reaching
        for this actually wants.
      */}
      {studioLive && (
        <span className="meta-caps text-faint" data-monitor-in-studio>
          monitor in studio
        </span>
      )}

      {/*
        The room's own settings, which used to occupy a third of the deck
        column permanently. They are decided once a set; the queue is watched
        continuously. A dialog is the right price for the first and the wrong
        one for the second.
      */}
      <Button variant="ghost" onClick={onSettings} data-room-settings-open>
        Room settings
      </Button>

      {/*
        Where the sound comes from, as a choice rather than a second button.

        This was a "Studio" toggle sitting beside "Go on air", which read as two
        ways to start broadcasting and was neither: one opened a panel, the
        other opened the room - and the studio path then made you press go-on-air
        a second time inside the panel.

        A broadcast has a *source*; that is what the column in the database
        says, and it is what this asks. Picking Studio opens the panel so the
        camera can be set up and previewed before anything is live. Once on air
        the same control switches between them, which the server does without
        dropping a listener - see `StationRepository::switchSource`.
      */}
      {studio && (
        <div
          className="flex shrink-0 items-center gap-1 rounded-[var(--radius-pill)] border border-line p-1"
          role="group"
          aria-label="Broadcast source"
          data-source-mode
        >
          {(
            [
              { id: "audio", label: "Audio" },
              { id: "studio", label: "Studio" },
            ] as const
          ).map((option) => (
            <button
              key={option.id}
              type="button"
              aria-pressed={mode === option.id}
              data-source={option.id}
              disabled={switching}
              onClick={() => onMode(option.id)}
              className={`rounded-[var(--radius-pill)] px-3.5 py-1.5 text-[13px] font-medium transition-colors disabled:opacity-60 ${
                mode === option.id
                  ? "bg-accent text-accent-ink"
                  : "text-soft hover:bg-raised hover:text-ink"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      <Button
        variant={room.status === "live" ? "danger" : "primary"}
        loading={broadcast.isPending || switching}
        onClick={() => broadcast.mutate()}
        data-broadcast-toggle
      >
        {room.status === "live" ? "End broadcast" : "Go on air"}
      </Button>
    </header>
  );
}

function Stat({
  value,
  label,
  tone = "text-ink",
}: {
  value: number;
  label: string;
  tone?: string;
}) {
  return (
    <div className="flex flex-col">
      <span className={`text-lg font-bold ${tone}`}>
        {value.toLocaleString()}
      </span>
      <span className="font-mono text-[10px] text-faint">{label}</span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Deck                                                                        */
/* -------------------------------------------------------------------------- */

function Deck({ room, onChange }: { room: Room; onChange: () => void }) {
  const [position, setPosition] = useState(0);
  const [reviewing, setReviewing] = useState(false);

  /*
   * The deck's clock runs locally between server updates.
   *
   * Asking the server four times a second for a number it already told us how
   * to compute would be absurd; `positionNow` extrapolates from the last known
   * position and the corrected clock, and every poll or pushed event snaps it
   * back to the truth.
   */
  useEffect(() => {
    const update = () => {
      const at = positionNow(room) ?? 0;
      const length = room.now_playing?.duration_ms;

      /*
       * Clamped to the track's length.
       *
       * The server advances a finished room on the next read, so this only ever
       * shows for about a second - but a bar reading 3:39 of 3:15 is wrong for
       * that second, and the reader has no way to know it is about to correct
       * itself.
       */
      setPosition(length == null ? at : Math.min(at, length));
    };

    update();
    const timer = setInterval(update, 250);

    return () => clearInterval(timer);
  }, [room]);

  const transport = useMutation({
    mutationFn: (action: "play" | "pause" | "next") => rooms.transport(action),
    onSuccess: onChange,
  });

  const live = room.status === "live";
  const track = room.now_playing;
  const remaining =
    track?.duration_ms == null
      ? null
      : Math.max(0, track.duration_ms - position);

  /*
   * Held: the track finished and auto-play is off, so the room is waiting on
   * the host rather than stalled. Worth distinguishing on the deck, because the
   * controls mean different things - play takes the next track rather than
   * resuming this one, which the server does and the button should say.
   */
  const held = room.holding;

  return (
    <div className="flex flex-col gap-4">
      {/*
        Said plainly, because silence in a live room is alarming and the host
        needs to know instantly whether it is their doing. This is the state
        auto-play off exists to produce, so it reads as a step rather than a
        fault - and it names the thing to do next.
      */}
      {held && (
        <p
          data-held
          className="rounded-[var(--radius-control)] border border-fresh-edge bg-fresh-wash px-3 py-2.5 font-mono text-[11px] leading-relaxed text-fresh"
        >
          Finished. The room is holding — review it, then play the next.
        </p>
      )}

      {/*
        Laid out across rather than down.

        In a column the sleeve wants to be the width of the column; at the top
        of the queue's pane it is a thumbnail beside the words, and the height
        it does not take is height the queue does. One row, so the queue starts
        as high up the screen as it can.
      */}
      <div className="flex items-start gap-5">
        {track ? (
          <Artwork
            track={{
              title: track.title,
              artwork: track.artwork,
              artwork_small: track.artwork_small,
            }}
            large
            className="aspect-square w-[108px] shrink-0 rounded-[var(--radius-card)]"
          />
        ) : (
          <div className="flex aspect-square w-[108px] shrink-0 items-center justify-center rounded-[var(--radius-card)] border border-line bg-surface">
            <span className="meta-caps text-ghost">idle</span>
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <span className="meta-caps text-faint">
                {held ? "Deck · finished" : "Deck · now playing"}
              </span>

              {track ? (
                <>
                  <h2 className="truncate text-xl font-bold leading-tight tracking-tight text-ink">
                    {track.title}
                  </h2>
                  <DrawerLink
                    to={`/${track.artist.username}`}
                    className="block truncate text-[14px] text-text-2 hover:text-ink hover:underline"
                  >
                    {track.artist.display_name}
                  </DrawerLink>
                </>
              ) : (
                <p className="mt-1 text-sm text-soft">
                  {live
                    ? "Nothing playing. Press play to take the next submission."
                    : "The room is off air."}
                </p>
              )}
            </div>

            <span className="shrink-0 font-mono text-[11px] text-faint">
              {room.source}
            </span>
          </div>

          {/* A plain elapsed bar, not the design's waveform. The waveform peaks
              are already fetched per track for the dock; wiring them here is
              worth doing once the deck is otherwise settled, and a progress bar
              that is accurate beats a waveform that is decorative. */}
          {track && (
            <div className="flex flex-col gap-1.5">
              <div className="h-1 overflow-hidden rounded-[var(--radius-pill)] bg-[#22224a]">
                <div
                  className="h-full rounded-[var(--radius-pill)] bg-accent transition-[width] duration-200"
                  style={{
                    width: `${track.duration_ms ? Math.min(100, (position / track.duration_ms) * 100) : 0}%`,
                  }}
                />
              </div>
              <div className="flex justify-between font-mono text-[11px] text-soft">
                <span data-deck-position>{duration(position)}</span>
                <span>
                  {remaining === null ? "—" : `-${duration(remaining)}`}
                </span>
              </div>
            </div>
          )}

          {/*
            The transport and what-about-this-track on one row.

            They were two stacked blocks in the old column because there was
            width for nothing else. Here there is - and a host reaching for Skip
            should not have to look past a heart to find it, so Skip sits beside
            Play and the track's own actions sit apart, at the end.
          */}
          <div className="flex flex-wrap items-center gap-2.5">
            <Button
              disabled={!live}
              loading={transport.isPending}
              onClick={() =>
                transport.mutate(room.paused || !track ? "play" : "pause")
              }
              data-transport-play
            >
              {held ? "Play next" : room.paused || !track ? "Play" : "Pause"}
            </Button>
            <Button
              variant="secondary"
              disabled={!live}
              onClick={() => transport.mutate("next")}
              data-transport-next
            >
              Skip
            </Button>

            {/*
              The design draws Follow here too. It is absent because
              `FollowButton` needs a loaded profile and the deck has a username -
              fetching one per track change to render a button nobody presses
              mid-set is the wrong trade. The artist's name above is a link to
              where following lives.
            */}
            {track && (
              <div className="ml-auto flex items-center gap-2.5">
                <FavoriteButton
                  trackId={track.id}
                  favorited={false}
                  className="justify-center rounded-[var(--radius-control)] border border-line px-4 py-2.5"
                />
                <button
                  type="button"
                  aria-pressed={reviewing}
                  data-review-toggle
                  onClick={() => setReviewing((open) => !open)}
                  className={`rounded-[var(--radius-control)] border px-4 py-2.5 text-[13px] font-bold transition-colors ${
                    reviewing
                      ? "border-transparent bg-accent text-accent-ink"
                      : "border-line text-ink hover:border-line-strong"
                  }`}
                >
                  Review
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {transport.isError && (
        <Alert tone="error">
          {transport.error instanceof ApiError
            ? transport.error.message
            : "The deck did not respond."}
        </Alert>
      )}

      {/*
        Keyed on the track, so skipping mid-sentence does not leave one artist's
        review in a box now labelled with another's name. It closes the panel
        too - the words are gone either way, and a host who meant to keep them
        would rather find that out with an empty box than send them to the
        wrong person.
      */}
      {track && reviewing && (
        <ReviewPanel
          key={track.id}
          trackId={track.id}
          onClose={() => setReviewing(false)}
        />
      )}
    </div>
  );
}

export { Deck as ConsoleDeck };
